import { useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from './client';

export interface AudienceRule {
  id?: string;
  field: string;
  op:
    | 'contains'
    | 'not_contains'
    | 'equals'
    | 'not_equals'
    | 'starts_with'
    | 'ends_with'
    | 'in'
    | 'not_in'
    | 'gt'
    | 'gte'
    | 'lt'
    | 'lte'
    | 'exists'
    | 'missing'
    | 'within_days';
  value?: string | number | boolean | string[];
}

export interface Audience {
  id: string;
  org_id: string;
  name: string;
  type: string;
  rules: AudienceRule[];
  is_dynamic: boolean;
  member_count: number;
  last_synced_at: string | null;
  created_at: string;
  updated_at: string;
}

export function useAudiences() {
  return useQuery<{ data: Audience[] }>({
    queryKey: ['audiences'],
    queryFn: async () => {
      const { data } = await apiClient.get<{ data: Audience[] }>('/audiences');
      return data;
    },
  });
}

export function useAudience(id: string | undefined) {
  return useQuery<Audience>({
    queryKey: ['audiences', id],
    queryFn: async () => {
      const { data } = await apiClient.get<{ data: Audience }>(`/audiences/${id}`);
      return data.data;
    },
    enabled: !!id,
  });
}

export interface RuleGroup {
  combinator: 'AND' | 'OR';
  include: AudienceRule[];
  exclude: AudienceRule[];
}

export interface AudienceCreatePayload {
  name: string;
  type: string;
  rules?: RuleGroup | AudienceRule[];
  is_dynamic?: boolean;
}

export function useCreateAudience() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: AudienceCreatePayload) => {
      const { data } = await apiClient.post<{ data: Audience }>('/audiences', payload);
      return data.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['audiences'] }),
  });
}

export function useUpdateAudience(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: Partial<AudienceCreatePayload>) => {
      const { data } = await apiClient.patch<{ data: Audience }>(`/audiences/${id}`, payload);
      return data.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['audiences'] });
      qc.invalidateQueries({ queryKey: ['audiences', id] });
    },
  });
}

export interface AudiencePreview {
  members: { id: string; name: string; email: string; title?: string; company?: string }[];
  total: number;
  sampled: boolean;
}

export function useAudiencePreview(id: string | undefined) {
  return useQuery<AudiencePreview>({
    queryKey: ['audiences', id, 'preview'],
    queryFn: async () => {
      const { data } = await apiClient.get<{ data: AudiencePreview }>(`/audiences/${id}/preview`);
      return data.data;
    },
    enabled: !!id,
  });
}

/**
 * Previews an *unsaved* rule draft. Backs the audience builder's "Review"
 * button so the user can refine rules and see the live match count without
 * persisting anything — saving is an explicit, separate action.
 */
export function useAudienceDraftPreview() {
  return useMutation({
    mutationFn: async (payload: { type: string; rules: RuleGroup }) => {
      const { data } = await apiClient.post<{ data: AudiencePreview }>('/audiences/preview', payload);
      return data.data;
    },
  });
}

export interface AvailableList {
  id: string;
  name: string;
  entity_type: 'candidate' | 'contact';
}

/**
 * Hook backing the "On list" rule dropdown — returns JT lists the org has
 * synced for the given entity type. Cached for a minute because list
 * metadata changes rarely.
 */
export function useAvailableLists(entityType: 'candidate' | 'contact' | undefined) {
  return useQuery<AvailableList[]>({
    queryKey: ['audiences', 'lists', entityType ?? null],
    queryFn: async () => {
      const { data } = await apiClient.get<{ data: AvailableList[] }>('/audiences/lists', {
        params: entityType ? { entity_type: entityType } : undefined,
      });
      return data.data;
    },
    enabled: !!entityType,
    staleTime: 60_000,
  });
}

/** Candidate and contact lists together, by name: for pickers that don't know the record type. */
export function useAllAvailableLists(): AvailableList[] {
  const { data: candidateLists } = useAvailableLists('candidate');
  const { data: contactLists } = useAvailableLists('contact');
  return useMemo(
    () => [...(candidateLists ?? []), ...(contactLists ?? [])].sort((a, b) => a.name.localeCompare(b.name)),
    [candidateLists, contactLists]
  );
}
